import React, { useContext, useState } from 'react';
import { Button } from '../shared/Button';
import { Input } from '../shared/inputs/Input';
import { useNavigate } from 'react-router-dom';
import { postAdminLogin } from '../../services/AccountService';
import { FormTexts, LoginTexts as Texts} from '../../helpers/LayoutTexts';
import { ResponseModel } from '../../models/ResponseModel';
import { LoginModel, LoginValidation } from '../../models/LoginModel';
import { AuthContext } from '../../providers/AuthContextProvider';
import { validateEmail } from '../../helpers/Common';
import { LoginFormLoader } from '../loaders/LoginFormLoader';
import useErrorPage from "../../hooks/useErrorPage";

interface Props {
    styles: any;  
    callbackUrl: string | null;
    message: string | null;    
    changeStage(stage: string): void;
    openAlert(status: string, message: string): void;
}

const initialLoginModel: LoginModel  = {
    email: "",
    password: "",
}

const initialLoginValidation: LoginValidation = {
    email: {},
    password: {}
}

export const Login: React.FC<Props> = (props) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loginModel, setLoginModel] = useState<LoginModel>(initialLoginModel);
    const [validation, setValidation] = useState<LoginValidation>(initialLoginValidation);
    const styles = props.styles;
    const authContext = useContext(AuthContext);
    const [toErrorPage] = useErrorPage();

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setLoginModel((values: any) => ({ ...values, [name]: value }));
        if(validation[name] && validation[name].invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);
    }

    const validateInput = (name: string, value: string) => {
        if (name === "email") {
            if (value === "") {
                setValidation((values: any) => ({ ...values, [name]: { valid: false, invalid: true, invalidMessage: "Email is required" } }));
            } else if (!validateEmail(value)) {
                setValidation((values: any) => ({ ...values, [name]: { valid: false, invalid: true, invalidMessage: "Invalid email address!" } }));
            } else {
                setValidation((values: any) => ({ ...values, [name]: { valid: true, invalid: false } }));
            }
        }
        if (name === "password") {
            if (value === "") {
                setValidation((values: any) => ({ ...values, [name]: { valid: false, invalid: true, invalidMessage: name + " is required" } }));
            } else {
                setValidation((values: any) => ({ ...values, [name]: { valid: true, invalid: false } }));
            }
        }        
    }

    const validateForm = (): number => {
        let formValidation: LoginValidation = {
            email: { valid: true },
            password: { valid: true }
        }
        if (loginModel.email === "") {
            formValidation.email = {valid: false, invalid: true, invalidMessage: "Email is required"};
        }
        else if (!validateEmail(loginModel.email)) {
            formValidation.email = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
        }
        if (loginModel.password === "") {
            formValidation.password = {valid: false, invalid: true, invalidMessage: "Password is required"};
        }
        setValidation(formValidation);
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount;
    }

    async function submitLogin(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = Object.keys(validation).filter(key => validation[key].valid).length;
        if (validInputCount < 2) {
            validInputCount = validateForm();
        }
        if(validInputCount === 2) {
            setLoading(true);
            await postAdminLogin(loginModel)
            .then((loginResponse: ResponseModel) => {
                setLoading(false);
                if (loginResponse.status) {
                    authContext.setLoggedAdmin(loginResponse.data, loginResponse.token);
                    if (props.callbackUrl) {
                        navigate(props.callbackUrl);
                    } else {
                        navigate('/');
                    }
                }
                else {
                    props.openAlert("error", loginResponse.message);
                }
            })
            .catch((error) => {
                toErrorPage(error);
            });
        }
        else {
            props.openAlert("error", FormTexts.submitError);
        }
    }
 
    return (
        <> 
            {loading ? <LoginFormLoader/> :
            <form style={styles.loginForm} onSubmit={submitLogin}>                
                {props.message && <div style={styles.callbackMessage}>{props.message}</div>}
                <p style={styles.boldTitle}>{"Admins Login"}</p>
                <Input
                    label={'Email *'}
                    type={'text'}
                    name={"email"}
                    onChange={handleInputChange}
                    value={loginModel.email}
                    validation={validation.email}
                    onBlur={handleInputBlur}
                />
                <Input
                    label={'Password *'}
                    styles={styles.loginPassword}
                    type={'password'}
                    name={"password"}
                    value={loginModel.password}
                    onChange={handleInputChange}
                    validation={validation.password}
                    onBlur={handleInputBlur}
                />                
                <Button style={styles.submitButton} type="submit" disabled={loading}>Login</Button>                
            </form>}
        </>
    )
}